import * as React from "react";
import {
    Archive20Regular,
    ArrowClockwise20Regular,
    Dismiss20Regular,
    DocumentText20Regular,
    PersonAdd20Regular,
} from "@fluentui/react-icons";
import type { MailArchiveItem } from "../../services/mailArchiveService";
import {
    MAIL_ARCHIVE_FILTER,
    gruppiereNachTag,
    groesseLabel,
    istWiederholbar,
    statusFarbe,
    statusLabel,
    type MailArchiveFilter,
} from "./mailArchiveRows";
import { mailboxChipLabel } from "./mailSearch";
import { AUB, INK, INK2, LINE, BG_SOFT, GOLD, ACTIVE_BG } from "../chat/chatTheme";

/**
 * Cluster ABLAGE (M3) — die Ansicht „Ablage".
 *
 * Ersetzt (Desktop + Tablet) den Listen-/Detailbereich, exakt wie es die
 * Schwesteransicht `MailMentionsView` tut. Sie ist die NACHARBEITSLISTE der
 * dauerhaften Mail-Ablage: Mails ohne erkannten Kontakt (M3), fehlgeschlagene
 * Ablagen und — als eigener, ausdrücklich benannter Fall — die nach E11
 * übersprungenen (zu großen) Mails, die als einzige gar nicht gesichert sind.
 *
 * Farben aus `components/chat/chatTheme` und NICHT aus
 * `redesign/contactRedesign.css`: dessen Variablen sind unter `.kvp-rd`
 * gescopet, und die Mail-Oberfläche liegt nicht unter diesem Scope — sie wären
 * hier tot. Keine Fluent-UI-Formularkomponenten; Icons wie im Bestand.
 */
type MailArchiveViewProps = {
    items: MailArchiveItem[];
    loading: boolean;
    error: string | null;
    filter: MailArchiveFilter;
    onFilterChange: (f: MailArchiveFilter) => void;
    suche: string;
    onSucheChange: (v: string) => void;
    /** Öffnet die abgelegte `.eml` im vorhandenen Mail-Datei-Viewer. */
    onOpen: (item: MailArchiveItem) => void;
    onAssign: (item: MailArchiveItem) => void;
    onRetry: (item: MailArchiveItem) => void;
    onRefresh: () => void;
    onClose: () => void;
    /** Id der Zeile, die gerade neu eingereiht wird (Knopf sperren). */
    busyId?: number | null;
};

const fmtZeit = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
};

export const MailArchiveView: React.FC<MailArchiveViewProps> = ({
    items,
    loading,
    error,
    filter,
    onFilterChange,
    suche,
    onSucheChange,
    onOpen,
    onAssign,
    onRetry,
    onRefresh,
    onClose,
    busyId = null,
}) => {
    const gruppen = React.useMemo(() => gruppiereNachTag(items), [items]);

    const iconBtn: React.CSSProperties = {
        border: "none",
        background: "transparent",
        cursor: "pointer",
        color: INK2,
        display: "inline-flex",
        alignItems: "center",
        padding: 4,
        borderRadius: 6,
    };

    const pillBtn = (aktiv: boolean): React.CSSProperties => ({
        border: `1px solid ${aktiv ? AUB : LINE}`,
        background: aktiv ? ACTIVE_BG : "#fff",
        color: aktiv ? AUB : INK2,
        fontWeight: aktiv ? 700 : 600,
        fontSize: 12,
        padding: "3px 10px",
        borderRadius: 999,
        cursor: "pointer",
        whiteSpace: "nowrap",
    });

    const aktionBtn: React.CSSProperties = {
        border: `1px solid ${LINE}`,
        background: "#fff",
        color: AUB,
        fontSize: 11.5,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 6,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
    };

    const chipStyle: React.CSSProperties = {
        display: "inline-block",
        padding: "0 6px",
        borderRadius: 8,
        backgroundColor: ACTIVE_BG,
        color: AUB,
        fontSize: 10.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
    };

    const hintStyle: React.CSSProperties = {
        color: INK2,
        fontSize: 13,
        textAlign: "center",
        padding: "40px 16px",
        lineHeight: 1.6,
    };

    const renderZeile = (item: MailArchiveItem) => {
        const farbe = statusFarbe(item.status);
        const nacharbeit = item.contact === null || item.status === "failed" || item.status === "skipped";
        return (
            <div
                key={item.id}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${LINE}`,
                    borderLeft: nacharbeit ? `3px solid ${GOLD}` : `1px solid ${LINE}`,
                    marginBottom: 8,
                    background: "#fff",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: INK,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {item.subject || "Kein Betreff"}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <span
                            style={{
                                ...chipStyle,
                                backgroundColor: farbe.bg,
                                color: farbe.fg,
                            }}
                        >
                            {statusLabel(item.status)}
                        </span>
                        <span style={chipStyle}>{mailboxChipLabel(item.mailbox)}</span>
                        <span style={{ fontSize: 11, color: INK2, whiteSpace: "nowrap" }}>
                            {fmtZeit(item.receivedAt ?? item.createdAt)}
                        </span>
                    </span>
                </div>

                <div
                    style={{
                        fontSize: 12,
                        color: INK2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {item.senderName || item.senderEmail || "Unbekannter Absender"}
                    {item.senderName && item.senderEmail ? ` ‹${item.senderEmail.toLowerCase()}›` : ""}
                    {item.sizeBytes ? ` · ${groesseLabel(item.sizeBytes)}` : ""}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {item.contact ? (
                        <span style={chipStyle}>{item.contact.displayName}</span>
                    ) : (
                        <span style={{ ...chipStyle, backgroundColor: BG_SOFT, color: INK2 }}>Ohne Kontakt</span>
                    )}
                    {item.vorgang && <span style={chipStyle}>{item.vorgang.titel || `Vorgang ${item.vorgang.id}`}</span>}
                </div>

                {item.lastError && (
                    <div style={{ fontSize: 11.5, color: farbe.fg, lineHeight: 1.4 }}>{item.lastError}</div>
                )}

                <div style={{ display: "flex", gap: 6, marginTop: 2, flexWrap: "wrap" }}>
                    <button
                        type="button"
                        style={{ ...aktionBtn, opacity: item.documentId ? 1 : 0.45 }}
                        disabled={!item.documentId}
                        title={
                            item.documentId
                                ? "Abgelegte Mail öffnen"
                                : "Diese Mail ist noch nicht dauerhaft gesichert."
                        }
                        onClick={() => onOpen(item)}
                    >
                        <DocumentText20Regular style={{ fontSize: 14 }} />
                        Öffnen
                    </button>
                    <button type="button" style={aktionBtn} title="Kontakt zuweisen" onClick={() => onAssign(item)}>
                        <PersonAdd20Regular style={{ fontSize: 14 }} />
                        Kontakt zuweisen
                    </button>
                    {istWiederholbar(item) && (
                        <button
                            type="button"
                            style={{ ...aktionBtn, opacity: busyId === item.id ? 0.5 : 1 }}
                            disabled={busyId === item.id}
                            title="Ablage erneut versuchen"
                            onClick={() => onRetry(item)}
                        >
                            <ArrowClockwise20Regular style={{ fontSize: 14 }} />
                            {busyId === item.id ? "Läuft …" : "Erneut versuchen"}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderBody = () => {
        if (error) {
            return (
                <div style={{ ...hintStyle, color: "#a4262c" }} role="alert">
                    <div style={{ fontWeight: 600 }}>Die Ablage konnte nicht geladen werden</div>
                    <div style={{ marginTop: 4 }}>{error}</div>
                </div>
            );
        }
        if (loading && items.length === 0) return <div style={hintStyle}>Lädt …</div>;
        if (items.length === 0) {
            return (
                <div style={hintStyle}>
                    <Archive20Regular style={{ fontSize: 28, color: INK2, opacity: 0.7 }} />
                    <div style={{ marginTop: 10, fontWeight: 600, color: INK }}>Nichts nachzuarbeiten</div>
                    <div style={{ marginTop: 4 }}>
                        Archivierte Mails werden dauerhaft als Datei gesichert. Hier erscheinen nur die
                        Fälle, die noch Hand brauchen — ohne erkannten Kontakt, fehlgeschlagen oder zu
                        groß für die Ablage.
                    </div>
                </div>
            );
        }
        return (
            <>
                {gruppen.map((g) => (
                    <div key={g.label}>
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: INK2,
                                padding: "8px 2px 4px",
                                letterSpacing: "0.2px",
                            }}
                        >
                            {g.label}
                        </div>
                        {g.items.map(renderZeile)}
                    </div>
                ))}
            </>
        );
    };

    return (
        <div
            style={{
                flexGrow: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "10px 14px",
                    borderBottom: `1px solid ${LINE}`,
                    backgroundColor: BG_SOFT,
                    flexShrink: 0,
                }}
            >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <Archive20Regular style={{ color: AUB, flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: AUB }}>Ablage</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                    <button
                        type="button"
                        style={iconBtn}
                        title="Aktualisieren"
                        aria-label="Ablage aktualisieren"
                        onClick={onRefresh}
                    >
                        <ArrowClockwise20Regular />
                    </button>
                    <button
                        type="button"
                        style={iconBtn}
                        title="Schließen"
                        aria-label="Ablage schließen"
                        onClick={onClose}
                    >
                        <Dismiss20Regular />
                    </button>
                </span>
            </div>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderBottom: `1px solid ${LINE}`,
                    flexWrap: "wrap",
                    flexShrink: 0,
                }}
            >
                {MAIL_ARCHIVE_FILTER.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        style={pillBtn(filter === f.key)}
                        aria-pressed={filter === f.key}
                        onClick={() => onFilterChange(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
                <input
                    type="search"
                    value={suche}
                    placeholder="Betreff oder Absender …"
                    aria-label="Ablage durchsuchen"
                    onChange={(e) => onSucheChange(e.target.value)}
                    style={{
                        flex: 1,
                        minWidth: 160,
                        border: `1px solid ${LINE}`,
                        borderRadius: 6,
                        padding: "4px 8px",
                        fontSize: 12.5,
                        color: INK,
                        outline: "none",
                        background: "#fff",
                    }}
                />
            </div>

            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    padding: "10px 14px",
                    maxWidth: 820,
                    width: "100%",
                    margin: "0 auto",
                }}
            >
                {renderBody()}
            </div>
        </div>
    );
};

export default MailArchiveView;
